<script setup>
import { computed, reactive, ref, onMounted, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate } from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const { isWorkspaceV2 } = useWorkspaceV2();
const submitting = ref(false);
const opened = ref(false);
let resolveNavigation = null;
const saveError = ref('');
const discardRef = ref(null);
const initialState = ref('');
const dialogRef = ref(null);

const form = reactive({ name: '', domain: '', description: '' });

const isDirty = computed(() => JSON.stringify(form) !== initialState.value);
const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  form.name = '';
  form.domain = '';
  form.description = '';
};

const open = (company = {}) => {
  opened.value = true;
  form.name = company.name || '';
  form.domain = company.domain || '';
  form.description = company.description || '';
  initialState.value = JSON.stringify(form);
  saveError.value = '';
  submitting.value = false;
  dialogRef.value?.open();
};

const handleConfirm = () => {
  if (isFormInvalid.value || submitting.value || props.isLoading) return;
  submitting.value = true;
  saveError.value = '';

  emit('create', {
    name: form.name.trim(),
    domain: form.domain.trim() || null,
    description: form.description.trim() || null,
  });
};

const settleNavigation = allowed => {
  const resolve = resolveNavigation;
  resolveNavigation = null;
  resolve?.(allowed);
};
const discard = () => {
  settleNavigation(true);
  discardRef.value?.close();
  dialogRef.value?.close();
};
const closeDialog = () => {
  if (submitting.value || props.isLoading) return;
  if (isWorkspaceV2.value && isDirty.value) {
    discardRef.value?.open();
    return;
  }
  dialogRef.value?.close();
};
const onError = message => {
  submitting.value = false;
  saveError.value = message;
};

const onSuccess = () => {
  submitting.value = false;
  resetForm();
  dialogRef.value?.close();
};

const onClose = () => {
  opened.value = false;
  resetForm();
};
const guardNavigation = () => {
  if (!opened.value) return true;
  if (submitting.value || props.isLoading) return false;
  if (!isWorkspaceV2.value || !isDirty.value) return true;
  if (resolveNavigation) return false;
  discardRef.value?.open();
  return new Promise(resolve => {
    resolveNavigation = resolve;
  });
};
const preventUnload = event => {
  if (
    !opened.value ||
    (!submitting.value && !props.isLoading && !isDirty.value)
  )
    return;
  event.preventDefault();
  event.returnValue = '';
};
onBeforeRouteLeave(guardNavigation);
onBeforeRouteUpdate(guardNavigation);
onMounted(() => window.addEventListener('beforeunload', preventUnload));
onBeforeUnmount(() => {
  settleNavigation(false);
  window.removeEventListener('beforeunload', preventUnload);
});

defineExpose({ dialogRef, onSuccess, onError, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :width="isWorkspaceV2 ? 'lg' : '3xl'"
    :title="isWorkspaceV2 ? t('COMPANIES.CREATE.TITLE') : ''"
    overflow-y-auto
    :confirm-before-close="isWorkspaceV2 || submitting"
    @request-close="closeDialog"
    @confirm="handleConfirm"
    @close="onClose"
  >
    <fieldset
      class="m-0 flex min-w-0 flex-col gap-6 border-0 p-0"
      :class="
        isWorkspaceV2
          ? '[&_input]:!h-11 [&_input]:!rounded-xl [&_textarea]:!rounded-xl'
          : ''
      "
      :disabled="isLoading || submitting"
    >
      <div class="flex flex-col items-start gap-2">
        <span
          v-if="!isWorkspaceV2"
          class="py-1 text-sm font-medium text-n-slate-12"
        >
          {{ t('COMPANIES.CREATE.TITLE') }}
        </span>
        <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex min-w-0 flex-col gap-1.5">
            <label
              v-if="isWorkspaceV2"
              for="company-name"
              class="text-sm text-n-slate-11"
              ><span>{{
                t('COMPANIES.DETAIL.PROFILE.FIELDS.NAME')
              }}</span></label
            >
            <Input
              id="company-name"
              v-model="form.name"
              :placeholder="t('COMPANIES.DETAIL.PROFILE.FIELDS.NAME')"
              :disabled="isLoading"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
              autofocus
            />
          </div>
          <div class="flex min-w-0 flex-col gap-1.5">
            <label
              v-if="isWorkspaceV2"
              for="company-domain"
              class="text-sm text-n-slate-11"
              ><span>{{
                t('COMPANIES.DETAIL.PROFILE.FIELDS.DOMAIN')
              }}</span></label
            >
            <Input
              id="company-domain"
              v-model="form.domain"
              :placeholder="t('COMPANIES.DETAIL.PROFILE.FIELDS.DOMAIN')"
              :disabled="isLoading"
              custom-input-class="h-8 !pt-1 !pb-1 [&:not(.error,.focus)]:!outline-transparent"
            />
          </div>
        </div>
      </div>
      <label
        v-if="isWorkspaceV2"
        for="company-description"
        class="text-sm text-n-slate-11"
        ><span>{{
          t('COMPANIES.DETAIL.PROFILE.DESCRIPTION_PLACEHOLDER')
        }}</span></label
      >
      <TextArea
        id="company-description"
        v-model="form.description"
        :placeholder="t('COMPANIES.DETAIL.PROFILE.DESCRIPTION_PLACEHOLDER')"
        :disabled="isLoading"
        :max-length="280"
        class="w-full"
        show-character-count
        auto-height
      />
    </fieldset>
    <p
      v-if="isWorkspaceV2 && saveError"
      role="alert"
      class="text-sm text-n-ruby-11"
    >
      {{ saveError }}
    </p>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          :class="isWorkspaceV2 ? '!h-11 !rounded-xl' : ''"
          @click="closeDialog"
        />
        <Button
          :label="t('COMPANIES.CREATE.ACTIONS.SAVE')"
          color="blue"
          :class="isWorkspaceV2 ? 'min-h-11 !rounded-xl' : ''"
          type="submit"
          :disabled="isFormInvalid || isLoading || submitting"
          :is-loading="isLoading"
        />
      </div>
    </template>
  </Dialog>
  <Dialog
    ref="discardRef"
    type="alert"
    :title="t('WORKSPACE_V2.COMPANY_EDITOR.DISCARD_TITLE')"
    :description="t('WORKSPACE_V2.COMPANY_EDITOR.DISCARD_DESCRIPTION')"
    :confirm-button-label="t('WORKSPACE_V2.TASK_EDITOR.DISCARD')"
    :cancel-button-label="t('WORKSPACE_V2.TASK_EDITOR.KEEP_EDITING')"
    @confirm="discard"
    @close="settleNavigation(false)"
  />
</template>
